import { ChangeDetectionStrategy, Component } from '@angular/core'
import { isNotNil } from '@engineering11/utility'
import { ViewportService } from '@engineering11/web-utilities'
import { filter, map, startWith } from 'rxjs'
import { DEFAULT_JOB_NOTIFICATION_VALUE, IJobNotificationSettings, INotificationConfig, RequiredApplicationDetailKeys } from 'shared-lib'
import { EmployerStore } from '../../employer.store'

@Component({
  selector: 'job-application-settings',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngrxLet="saving$ as saving">
      <e11-panel *ngrxLet="loading$ as loading">
        <!-- Required Contact Details -->
        <ng-container *ngrxLet="employer$ as employer">
          <div class="e11-flex e11-justify-between e11-items-start">
            <required-contact-details
              (valueChanges)="requiredFields = $event"
              [loading]="loading || saving"
              [requiredFields]="employer?.requiredFields"
            ></required-contact-details>
            <e11-button
              [value]="(isMobile$ | async) ? ('Save' | translate) : ('Save changes' | translate)"
              color="primary-accent"
              size="sm"
              (click)="onSubmit()"
              [disabled]="loading || saving"
              [loading]="saving"
            ></e11-button>
          </div>
          <e11-divider />
        </ng-container>

        <div class="e11-flex e11-justify-between e11-items-center e11-mb-4">
          <h3
            class="e11-font-primary-bold"
            [ngClass]="{
              'e11-skeleton e11-text-transparent': loading
            }"
          >
            {{ 'Automated Opening Notifications' | translate }}
          </h3>
        </div>

        <div class="e11-flex e11-flex-col e11-gap-6" *ngrxLet="initialData$ as initialValue">
          <!-- JOB POST CLOSURE -->
          <job-notification-config-form
            [titleText]="'Opening Closure Notification' | translate"
            [subtitleText]="
              'Would you like to notify all candidates when an opening has been closed? Select the types of notifications to send.' | translate
            "
            [initialValue]="initialValue?.jobPostClosure || defaultValue"
            (onValueChange)="handleValueChange($event, 'jobPostClosure')"
            [loading]="loading || saving"
            [disabled]="loading || saving"
          ></job-notification-config-form>
          <!-- CANDIDATE REJECTION -->
          <job-notification-config-form
            [titleText]="'Candidate Rejection Notification' | translate"
            [subtitleText]="
              'Would you like to notify each candidate when they are no longer being considered for a position? Select the types of notifications to send.'
                | translate
            "
            [initialValue]="initialValue?.candidateRejection || defaultValue"
            (onValueChange)="handleValueChange($event, 'candidateRejection')"
            [loading]="loading || saving"
            [disabled]="loading || saving"
          ></job-notification-config-form>
        </div>
      </e11-panel>
    </ng-container>
  `,
})
export class JobApplicationSettingsComponent {
  formData: IJobNotificationSettings = {
    candidateRejection: DEFAULT_JOB_NOTIFICATION_VALUE,
    jobPostClosure: DEFAULT_JOB_NOTIFICATION_VALUE,
  }
  requiredFields?: RequiredApplicationDetailKeys[]

  defaultValue = DEFAULT_JOB_NOTIFICATION_VALUE

  isMobile$ = this.viewportService.viewportSizeChanged$.pipe(
    map(data => data == 'sm' || data == 'xs'),
    filter(isNotNil)
  )

  handleValueChange(value: INotificationConfig, field: keyof IJobNotificationSettings) {
    this.formData = {
      ...this.formData,
      [field]: value,
    }
  }

  constructor(private employerStore: EmployerStore, private viewportService: ViewportService) {}

  onSubmit() {
    const updatedData = {
      jobNotificationSettings: this.formData,
      requiredFields: this.requiredFields,
    }
    this.employerStore.onUpdateEmployer(updatedData)
  }
  employer$ = this.employerStore.employer$
  initialData$ = this.employerStore.employer$.pipe(
    map(employer => employer?.jobNotificationSettings),
    startWith(undefined)
  )
  loading$ = this.employerStore.employerLoaded$.pipe(
    map(loaded => !loaded),
    startWith(true)
  )
  saving$ = this.employerStore.employerUpdating$.pipe(startWith(false))
}
